import React from 'react';
import { Calendar, Clock, Star, CheckCircle, XCircle, ArrowRight, AlertCircle, Scale, BookOpen, Search, FileText, Zap, Shield, TrendingUp } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { SEOHead } from '@/components/SEOHead';
import { useLanguage } from '@/contexts/LanguageContext';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';

export const TestLexisAIPage = () => {
  const { language } = useLanguage();

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": language === 'fr' ? "Nous avons testé Lexis AI+ pour vous" : "We Have Tested Lexis AI+ for You",
    "description": language === 'fr' ? "Test complet de Lexis AI+, l'IA intégrée à LexisNexis pour les professionnels du droit" : "Complete test of Lexis AI+ integrated with LexisNexis for legal professionals",
    "author": { "@type": "Organization", "name": "Gaius" },
    "publisher": { "@type": "Organization", "name": "Gaius", "logo": { "@type": "ImageObject", "url": "https://gaius-tech.com/logo.png" } },
    "datePublished": "2026-01-17",
    "dateModified": "2026-01-17"
  };

  if (language === 'fr') {
    return (
      <div className="min-h-screen bg-background">
        <SEOHead 
          title="Lexis AI+ Avocat 2026 : Test Complet (Avis, Prix, Comparatif)" 
          description="Test Lexis AI+ pour avocats 2026 : fonctionnalités, tarifs, comparatif Harvey et Doctrine. Notre avis après plusieurs semaines de test." 
          keywords="lexis ai+, lexisnexis ia, ia générative avocats, lexis nexis intelligence artificielle, recherche juridique ia, legal ai france" 
          canonicalUrl="https://gaius-tech.com/articles/test-lexis-ai" 
          alternateUrlFr="https://gaius-tech.com/articles/test-lexis-ai"
          alternateUrlEn="https://gaius-tech.com/articles/test-lexis-ai"
          pageType="article"
          structuredData={structuredData}
        />
        
        {/* Header */}
        <div className="bg-white border-b">
          <div className="max-w-4xl mx-auto px-4 py-12 text-center">
            <Badge variant="secondary" className="mb-4 bg-blue-100 text-blue-800">AI News</Badge>
            <h1 className="text-4xl lg:text-5xl font-bold text-foreground mb-6">
              Lexis AI+ pour Avocats : Test Complet 2026 (Prix, Avis, Comparatif avec Harvey et Doctrine)
            </h1>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto mb-8">
              L'IA générative intégrée à l'écosystème LexisNexis : quand la puissance documentaire rencontre l'intelligence artificielle.
            </p>
            <div className="flex items-center justify-center space-x-6 text-sm text-muted-foreground">
              <span className="flex items-center space-x-1">
                <Calendar className="w-4 h-4" />
                <span>17 janvier 2026</span>
              </span>
              <span className="flex items-center space-x-1">
                <Clock className="w-4 h-4" />
                <span>16 min de lecture</span>
              </span>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="max-w-4xl mx-auto px-4 py-12">
          <div className="prose prose-lg max-w-none">
            
            {/* Introduction */}
            <p className="text-xl leading-relaxed mb-8">
              <strong>LexisNexis</strong>, acteur historique de l'information juridique, a lancé <strong>Lexis AI+</strong> pour intégrer l'intelligence artificielle générative à sa base documentaire de référence. Nous avons testé cette solution pendant plusieurs semaines dans un contexte de pratique intensive du droit des affaires.
            </p>

            <p className="mb-8">
              Pour les cabinets déjà équipés de solutions LexisNexis, la question se pose naturellement : <strong>Lexis AI+ justifie-t-il l'investissement supplémentaire ?</strong> Notre verdict après un test approfondi.
            </p>

            {/* Key Stats */}
            <div className="bg-primary/10 border-l-4 border-primary p-6 my-8">
              <div className="flex items-start">
                <TrendingUp className="w-6 h-6 text-primary mt-1 mr-4 flex-shrink-0" />
                <div>
                  <h4 className="text-primary font-semibold text-lg mb-2">Chiffres clés de notre test</h4>
                  <ul className="text-foreground/80 space-y-1 mb-0">
                    <li>• <strong>87%</strong> de sources correctement citées et vérifiables</li>
                    <li>• <strong>-45%</strong> de temps sur la recherche documentaire</li>
                    <li>• <strong>95%</strong> de satisfaction sur la qualité des références</li>
                    <li>• <strong>3 secondes</strong> en moyenne pour une réponse complète</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Section 1 */}
            <h2 className="text-3xl font-bold mt-12 mb-6">Qu'est-ce que Lexis AI+ ? L'IA intégrée au fonds LexisNexis</h2>

            <p className="mb-6">
              Ce qui distingue fondamentalement <strong>Lexis AI+</strong> des outils généralistes comme ChatGPT ou Claude, c'est son ancrage dans le <strong>fonds documentaire LexisNexis</strong>. Chaque réponse générée par l'IA est directement issue de sources identifiées : jurisprudence, doctrine, textes législatifs et réglementaires.
            </p>

            <p className="mb-6">
              Cette approche <strong>RAG (Retrieval-Augmented Generation)</strong> garantit une traçabilité que les modèles généralistes ne peuvent offrir. Lorsque Lexis AI+ affirme une règle juridique, il indique précisément d'où elle provient.
            </p>

            <h3 className="text-2xl font-bold mt-10 mb-4">Architecture technique</h3>

            <p className="mb-6">
              Lexis AI+ repose sur une architecture hybride combinant :
            </p>

            <ul className="list-disc pl-6 space-y-3 mb-8">
              <li><strong>Un moteur de recherche sémantique</strong> : identification des sources pertinentes dans la base LexisNexis</li>
              <li><strong>Un LLM de synthèse</strong> : génération de réponses structurées à partir des sources identifiées</li>
              <li><strong>Un système de citation automatique</strong> : référencement précis de chaque affirmation</li>
              <li><strong>Une couche de vérification</strong> : contrôle de cohérence entre la réponse et les sources</li>
            </ul>

            {/* Section 2 */}
            <h2 className="text-3xl font-bold mt-12 mb-6">Test Lexis AI+ : fonctionnalités pour avocats et juristes</h2>

            <h3 className="text-2xl font-bold mt-10 mb-4">1. Recherche juridique assistée</h3>

            <p className="mb-6">
              La fonctionnalité phare de Lexis AI+ est la <strong>recherche conversationnelle</strong>. Plutôt que de formuler des requêtes booléennes complexes, l'utilisateur pose une question en langage naturel.
            </p>

            <div className="bg-muted/50 p-6 rounded-lg my-8">
              <h4 className="font-semibold mb-4">Exemple de requête testée :</h4>
              <p className="italic mb-4">"Quelles sont les conditions de validité d'une clause de non-concurrence dans un contrat de travail selon la jurisprudence récente de la Cour de cassation ?"</p>
              <p className="text-sm text-muted-foreground">
                <strong>Résultat :</strong> Réponse structurée en 4 points avec 7 décisions de la Chambre sociale citées (2022-2025), liens directs vers les arrêts complets.
              </p>
            </div>

            <h3 className="text-2xl font-bold mt-10 mb-4">2. Synthèse de jurisprudence</h3>

            <p className="mb-6">
              Lexis AI+ excelle dans la <strong>synthèse jurisprudentielle</strong>. L'outil identifie les grandes tendances, les revirements, les divergences entre juridictions.
            </p>

            <p className="mb-6">
              Nous l'avons testé sur des contentieux complexes (responsabilité des dirigeants, clauses abusives B2B, RGPD). À chaque fois, la synthèse était fidèle aux sources et correctement nuancée.
            </p>

            <h3 className="text-2xl font-bold mt-10 mb-4">3. Analyse comparative</h3>

            <p className="mb-6">
              Une fonctionnalité particulièrement utile : la <strong>comparaison de régimes juridiques</strong>. Lexis AI+ peut croiser les approches françaises et européennes sur un même sujet.
            </p>

            <h3 className="text-2xl font-bold mt-10 mb-4">4. Veille juridique intelligente</h3>

            <p className="mb-6">
              L'outil permet de configurer des <strong>alertes contextuelles</strong> qui ne se limitent pas aux mots-clés mais comprennent le sens des évolutions jurisprudentielles.
            </p>

            {/* Comparison Table */}
            <h2 className="text-3xl font-bold mt-12 mb-6">Lexis AI+ vs Harvey AI vs ChatGPT : comparatif IA juridique</h2>

            <div className="overflow-x-auto my-8">
              <table className="w-full border-collapse border border-border">
                <thead>
                  <tr className="bg-muted">
                    <th className="border border-border p-3 text-left">Critère</th>
                    <th className="border border-border p-3 text-center">Lexis AI+</th>
                    <th className="border border-border p-3 text-center">Harvey</th>
                    <th className="border border-border p-3 text-center">ChatGPT</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="border border-border p-3 font-medium">Sources vérifiables</td>
                    <td className="border border-border p-3 text-center">⭐⭐⭐⭐⭐</td>
                    <td className="border border-border p-3 text-center">⭐⭐⭐⭐</td>
                    <td className="border border-border p-3 text-center">⭐⭐</td>
                  </tr>
                  <tr className="bg-muted/30">
                    <td className="border border-border p-3 font-medium">Droit français</td>
                    <td className="border border-border p-3 text-center">⭐⭐⭐⭐⭐</td>
                    <td className="border border-border p-3 text-center">⭐⭐⭐</td>
                    <td className="border border-border p-3 text-center">⭐⭐</td>
                  </tr>
                  <tr>
                    <td className="border border-border p-3 font-medium">Actualité jurisprudentielle</td>
                    <td className="border border-border p-3 text-center">⭐⭐⭐⭐⭐</td>
                    <td className="border border-border p-3 text-center">⭐⭐⭐⭐</td>
                    <td className="border border-border p-3 text-center">⭐</td>
                  </tr>
                  <tr className="bg-muted/30">
                    <td className="border border-border p-3 font-medium">Rédaction contractuelle</td>
                    <td className="border border-border p-3 text-center">⭐⭐⭐</td>
                    <td className="border border-border p-3 text-center">⭐⭐⭐⭐⭐</td>
                    <td className="border border-border p-3 text-center">⭐⭐⭐</td>
                  </tr>
                  <tr>
                    <td className="border border-border p-3 font-medium">Rapport qualité/prix</td>
                    <td className="border border-border p-3 text-center">⭐⭐⭐</td>
                    <td className="border border-border p-3 text-center">⭐⭐</td>
                    <td className="border border-border p-3 text-center">⭐⭐⭐⭐⭐</td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* Pros */}
            <h2 className="text-3xl font-bold mt-12 mb-6">Avis avocats : les points forts de Lexis AI+</h2>

            <div className="grid md:grid-cols-2 gap-4 my-8">
              <Card className="border-green-200 bg-green-50">
                <CardContent className="p-4">
                  <div className="flex items-start space-x-3">
                    <CheckCircle className="w-5 h-5 text-green-600 mt-1 flex-shrink-0" />
                    <div>
                      <h4 className="font-semibold text-green-800">Fiabilité des sources</h4>
                      <p className="text-sm text-green-700">Chaque affirmation est rattachée à une source identifiable dans le fonds LexisNexis.</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card className="border-green-200 bg-green-50">
                <CardContent className="p-4">
                  <div className="flex items-start space-x-3">
                    <CheckCircle className="w-5 h-5 text-green-600 mt-1 flex-shrink-0" />
                    <div>
                      <h4 className="font-semibold text-green-800">Actualité garantie</h4>
                      <p className="text-sm text-green-700">Base mise à jour en continu, jurisprudence récente intégrée sous quelques jours.</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card className="border-green-200 bg-green-50">
                <CardContent className="p-4">
                  <div className="flex items-start space-x-3">
                    <CheckCircle className="w-5 h-5 text-green-600 mt-1 flex-shrink-0" />
                    <div>
                      <h4 className="font-semibold text-green-800">Intégration workflow</h4>
                      <p className="text-sm text-green-700">Parfaite intégration avec les autres outils LexisNexis (recherche, veille, bibliothèque).</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card className="border-green-200 bg-green-50">
                <CardContent className="p-4">
                  <div className="flex items-start space-x-3">
                    <CheckCircle className="w-5 h-5 text-green-600 mt-1 flex-shrink-0" />
                    <div>
                      <h4 className="font-semibold text-green-800">Expertise droit français</h4>
                      <p className="text-sm text-green-700">Couverture exceptionnelle du droit français : codes, jurisprudence, doctrine.</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Cons */}
            <h2 className="text-3xl font-bold mt-12 mb-6">Limites de Lexis AI+ : coût, verrouillage et rédaction</h2>

            <div className="grid md:grid-cols-2 gap-4 my-8">
              <Card className="border-red-200 bg-red-50">
                <CardContent className="p-4">
                  <div className="flex items-start space-x-3">
                    <XCircle className="w-5 h-5 text-red-600 mt-1 flex-shrink-0" />
                    <div>
                      <h4 className="font-semibold text-red-800">Coût cumulatif</h4>
                      <p className="text-sm text-red-700">S'ajoute à l'abonnement LexisNexis existant, budget conséquent pour les petites structures.</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card className="border-red-200 bg-red-50">
                <CardContent className="p-4">
                  <div className="flex items-start space-x-3">
                    <XCircle className="w-5 h-5 text-red-600 mt-1 flex-shrink-0" />
                    <div>
                      <h4 className="font-semibold text-red-800">Périmètre documentaire</h4>
                      <p className="text-sm text-red-700">Limité au fonds LexisNexis, pas d'accès aux sources externes ou bases concurrentes.</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card className="border-red-200 bg-red-50">
                <CardContent className="p-4">
                  <div className="flex items-start space-x-3">
                    <XCircle className="w-5 h-5 text-red-600 mt-1 flex-shrink-0" />
                    <div>
                      <h4 className="font-semibold text-red-800">Rédaction limitée</h4>
                      <p className="text-sm text-red-700">Moins performant que Harvey ou Claude pour la génération de documents complexes.</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card className="border-red-200 bg-red-50">
                <CardContent className="p-4">
                  <div className="flex items-start space-x-3">
                    <XCircle className="w-5 h-5 text-red-600 mt-1 flex-shrink-0" />
                    <div>
                      <h4 className="font-semibold text-red-800">Verrouillage écosystème</h4>
                      <p className="text-sm text-red-700">Renforce la dépendance à l'écosystème LexisNexis, difficile de migrer ensuite.</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Use Cases */}
            <h2 className="text-3xl font-bold mt-12 mb-6">Pour quels avocats Lexis AI+ est-il fait ?</h2>

            <h3 className="text-2xl font-bold mt-10 mb-4">✅ Lexis AI+ excelle pour :</h3>

            <ul className="list-disc pl-6 space-y-3 mb-8">
              <li><strong>La recherche jurisprudentielle approfondie</strong> : identifier l'état du droit sur une question précise</li>
              <li><strong>La veille juridique intelligente</strong> : suivre les évolutions d'un domaine spécifique</li>
              <li><strong>La préparation de contentieux</strong> : constituer un dossier de jurisprudences pertinentes</li>
              <li><strong>La formation des collaborateurs</strong> : réponses pédagogiques et sourcées</li>
            </ul>

            <h3 className="text-2xl font-bold mt-10 mb-4">⚠️ À compléter avec d'autres outils pour :</h3>

            <ul className="list-disc pl-6 space-y-3 mb-8">
              <li><strong>La rédaction contractuelle</strong> : préférer Harvey ou Claude</li>
              <li><strong>L'analyse de documents volumineux</strong> : combiner avec des outils spécialisés</li>
              <li><strong>Le droit international</strong> : sources limitées hors droit français/européen</li>
            </ul>

            {/* Pricing */}
            <h2 className="text-3xl font-bold mt-12 mb-6">Prix Lexis AI+ 2026 : tarifs et retour sur investissement</h2>

            <div className="bg-muted/50 p-6 rounded-lg my-8">
              <h4 className="font-semibold mb-4">Structure tarifaire (indicative)</h4>
              <ul className="space-y-2">
                <li>• <strong>Prérequis</strong> : abonnement LexisNexis actif</li>
                <li>• <strong>Module Lexis AI+</strong> : à partir de 150€/mois/utilisateur</li>
                <li>• <strong>Formule cabinet</strong> : tarifs dégressifs selon le nombre d'utilisateurs</li>
              </ul>
              <p className="mt-4 text-sm text-muted-foreground">
                <em>Contactez LexisNexis pour un devis personnalisé. Prix constatés en janvier 2026.</em>
              </p>
            </div>

            {/* Verdict */}
            <h2 className="text-3xl font-bold mt-12 mb-6">Notre verdict Lexis AI+ 2026 : note 4/5 pour avocats</h2>

            <div className="flex items-center space-x-2 my-6">
              {[1, 2, 3, 4, 5].map((i) => (
                <Star key={i} className={`w-8 h-8 ${i <= 4 ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'}`} />
              ))}
              <span className="ml-4 text-2xl font-bold">4/5</span>
            </div>

            <p className="mb-6">
              <strong>Lexis AI+</strong> représente l'évolution naturelle pour les cabinets déjà ancrés dans l'écosystème LexisNexis. Sa force réside dans la <strong>fiabilité absolue des sources</strong> et la <strong>traçabilité des réponses</strong>.
            </p>

            <p className="mb-6">
              Pour la <strong>recherche juridique pure</strong>, c'est probablement l'outil le plus fiable du marché français. En revanche, pour les tâches de <strong>rédaction ou d'analyse contractuelle</strong>, il devra être complété par d'autres solutions.
            </p>

            <div className="bg-primary/10 border-l-4 border-primary p-6 my-8">
              <div className="flex items-start">
                <Scale className="w-6 h-6 text-primary mt-1 mr-4 flex-shrink-0" />
                <div>
                  <h4 className="text-primary font-semibold text-lg mb-2">Recommandation Gaius</h4>
                  <p className="text-foreground/80 mb-0">
                    Lexis AI+ est <strong>indispensable</strong> pour les cabinets travaillant intensivement sur le droit français et déjà équipés LexisNexis. Pour les autres, évaluez d'abord si le fonds documentaire LexisNexis correspond à vos besoins avant d'investir dans l'IA.
                  </p>
                </div>
              </div>
            </div>

          </div>
          
          {/* CTA */}
          <div className="mt-12 pt-8 border-t">
            <div className="flex flex-col sm:flex-row gap-4">
              <Button asChild>
                <Link to="/training">
                  Se former à l'IA juridique <ArrowRight className="ml-2 w-4 h-4" />
                </Link>
              </Button>
              <Button variant="outline" asChild>
                <Link to="/blog">
                  Lire nos autres tests
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // English version
  return (
    <div className="min-h-screen bg-background">
      <SEOHead 
        title="Lexis AI+ for Lawyers: Complete Review" 
        description="Lexis AI+ review integrated with LexisNexis. Features, pricing, pros and cons for legal professionals." 
        keywords="lexis ai+, lexisnexis ai, legal ai, generative ai lawyers, legal research ai" 
        canonicalUrl="https://gaius-tech.com/articles/test-lexis-ai" 
        alternateUrlFr="https://gaius-tech.com/articles/test-lexis-ai"
        alternateUrlEn="https://gaius-tech.com/articles/test-lexis-ai"
        pageType="article"
        structuredData={structuredData}
      />
      
      {/* Header */}
      <div className="bg-white border-b">
        <div className="max-w-4xl mx-auto px-4 py-12 text-center">
          <Badge variant="secondary" className="mb-4 bg-blue-100 text-blue-800">AI News</Badge>
            <h1 className="text-4xl lg:text-5xl font-bold text-foreground mb-6">
              Lexis AI+ for Lawyers: Complete 2026 Review (Pricing, Comparison with Harvey)
          </h1>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto mb-8">
            The generative AI integrated into the LexisNexis ecosystem: when documentary power meets artificial intelligence.
          </p>
          <div className="flex items-center justify-center space-x-6 text-sm text-muted-foreground">
            <span className="flex items-center space-x-1">
              <Calendar className="w-4 h-4" />
              <span>January 17, 2026</span>
            </span>
            <span className="flex items-center space-x-1">
              <Clock className="w-4 h-4" />
              <span>16 min read</span>
            </span>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-12">
        <div className="prose prose-lg max-w-none">
          
          <p className="text-xl leading-relaxed mb-8">
            <strong>LexisNexis</strong>, a historical player in legal information, launched <strong>Lexis AI+</strong> to integrate generative artificial intelligence with its reference documentary base. We tested this solution for several weeks in an intensive corporate law practice context.
          </p>

          <p className="mb-8">
            For firms already equipped with LexisNexis solutions, the question naturally arises: <strong>Does Lexis AI+ justify the additional investment?</strong> Our verdict after thorough testing.
          </p>

          {/* Key Stats */}
          <div className="bg-primary/10 border-l-4 border-primary p-6 my-8">
            <div className="flex items-start">
              <TrendingUp className="w-6 h-6 text-primary mt-1 mr-4 flex-shrink-0" />
              <div>
                <h4 className="text-primary font-semibold text-lg mb-2">Key figures from our test</h4>
                <ul className="text-foreground/80 space-y-1 mb-0">
                  <li>• <strong>87%</strong> of sources correctly cited and verifiable</li>
                  <li>• <strong>-45%</strong> time on documentary research</li>
                  <li>• <strong>95%</strong> satisfaction on reference quality</li>
                  <li>• <strong>3 seconds</strong> average for a complete response</li>
                </ul>
              </div>
            </div>
          </div>

          <h2 className="text-3xl font-bold mt-12 mb-6">The DNA of Lexis AI+: The Power of Documentary Wealth</h2>

          <p className="mb-6">
            What fundamentally distinguishes <strong>Lexis AI+</strong> from generalist tools like ChatGPT or Claude is its anchoring in the <strong>LexisNexis documentary collection</strong>. Every response generated by the AI comes directly from identified sources: case law, doctrine, legislative and regulatory texts.
          </p>

          <p className="mb-6">
            This <strong>RAG (Retrieval-Augmented Generation)</strong> approach guarantees traceability that generalist models cannot offer. When Lexis AI+ states a legal rule, it indicates precisely where it comes from.
          </p>

          <h2 className="text-3xl font-bold mt-12 mb-6">Features Tested in Detail</h2>

          <h3 className="text-2xl font-bold mt-10 mb-4">1. Assisted Legal Research</h3>

          <p className="mb-6">
            The flagship feature of Lexis AI+ is <strong>conversational search</strong>. Rather than formulating complex Boolean queries, the user asks a question in natural language.
          </p>

          <h3 className="text-2xl font-bold mt-10 mb-4">2. Case Law Synthesis</h3>

          <p className="mb-6">
            Lexis AI+ excels in <strong>jurisprudential synthesis</strong>. The tool identifies major trends, reversals, and divergences between jurisdictions.
          </p>

          <h3 className="text-2xl font-bold mt-10 mb-4">3. Comparative Analysis</h3>

          <p className="mb-6">
            A particularly useful feature: <strong>comparison of legal regimes</strong>. Lexis AI+ can cross-reference French and European approaches on the same subject.
          </p>

          {/* Verdict */}
          <h2 className="text-3xl font-bold mt-12 mb-6">Our Final Verdict</h2>

          <div className="flex items-center space-x-2 my-6">
            {[1, 2, 3, 4, 5].map((i) => (
              <Star key={i} className={`w-8 h-8 ${i <= 4 ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'}`} />
            ))}
            <span className="ml-4 text-2xl font-bold">4/5</span>
          </div>

          <p className="mb-6">
            <strong>Lexis AI+</strong> represents the natural evolution for firms already anchored in the LexisNexis ecosystem. Its strength lies in the <strong>absolute reliability of sources</strong> and <strong>response traceability</strong>.
          </p>

          <p className="mb-6">
            For <strong>pure legal research</strong>, it's probably the most reliable tool on the French market. However, for <strong>drafting or contract analysis</strong> tasks, it will need to be complemented by other solutions.
          </p>

        </div>
        
        {/* CTA */}
        <div className="mt-12 pt-8 border-t">
          <div className="flex flex-col sm:flex-row gap-4">
            <Button asChild>
              <Link to="/training">
                Get trained in Legal AI <ArrowRight className="ml-2 w-4 h-4" />
              </Link>
            </Button>
            <Button variant="outline" asChild>
              <Link to="/blog">
                Read our other tests
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
